import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * /admin/settings/pricing — the prime rate gate.
 *
 * 🚨 THIS SCREEN EXISTS BECAUSE THE GATE NEEDED A RELEASE. backend/utils/
 * primeRate.js refuses to price a loan when the held prime predates an FOMC
 * decision, when it is not a multiple of 25bp, or when the FOMC calendar has
 * run out. FRED has been unreachable since 2026-04-13, so without a way for a
 * human to enter the current rate the gate would be an outage rather than a
 * control — and an alert telling somebody to visit a page that does not exist
 * is the same defect as the training task that pointed at the registration
 * route.
 *
 * ⚠️ IT IS NOT A BYPASS. What is typed here passes the SAME assertion as every
 * other write path — 6.80%, the value ten files were priced on, is refused from
 * this form too. What the form adds is the attribution the three hand-inserted
 * rows lacked: who entered it, and what publication they read it from.
 */
export default function AdminSettingsPricing() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('doorly_admin_token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [state, setState]   = useState(null);
  const [loading, setLoad]  = useState(true);
  const [err, setErr]       = useState('');
  const [rate, setRate]     = useState('');
  const [note, setNote]     = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [toast, setToast]   = useState(null);

  const load = useCallback(async () => {
    setLoad(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/api/admin/prime-rate`, { headers });
      setState(data);
    } catch (e) { setErr(e.response?.data?.error || 'Could not read the prime rate state.'); }
    finally { setLoad(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveErr('');
    try {
      const { data } = await axios.post(`${API}/api/admin/prime-rate`,
        { rate: Number(rate), source_note: note }, { headers });
      setState(data.state); setRate(''); setNote('');
      setToast(`Prime set to ${(data.rate * 100).toFixed(2)}%`);
      setTimeout(() => setToast(null), 4000);
    } catch (e2) { setSaveErr(e2.response?.data?.error || 'Could not save.'); }
    finally { setSaving(false); }
  };

  const KIND = {
    ok:                 { bg: '#f0fdf4', bd: '#acf44e', fg: '#166534', label: '✓ Pricing is open' },
    stale:              { bg: '#fff7ed', bd: '#fed7aa', fg: '#7a5200', label: '🛑 Pricing halted — the held rate predates an FOMC decision' },
    invalid:            { bg: '#fff0f3', bd: '#fecdd3', fg: '#9f1239', label: '🛑 Pricing halted — the held rate is not a possible prime rate' },
    calendar_exhausted: { bg: '#fff0f3', bd: '#fecdd3', fg: '#9f1239', label: '🛑 Pricing halted — the FOMC calendar has run out' },
    empty:              { bg: '#fff0f3', bd: '#fecdd3', fg: '#9f1239', label: '🛑 Pricing halted — no prime rate on record' },
  };
  const k = KIND[state?.kind] || KIND.empty;

  return (
    <AdminLayout>
      <div style={{ padding: '24px 28px', maxWidth: 820 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>Pricing — Prime Rate</h1>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.6 }}>
          Every priced file is built on this number: the base rate is prime + 2.00%. Prime moves only
          when the FOMC moves the target, and only in quarter points — so a value that is not a
          multiple of 0.25% is rejected here rather than stored, and a value written before a rate
          decision stops pricing until it is refreshed.
        </p>

        {loading && <div style={{ fontSize: 13, color: '#6b7280' }}>Loading…</div>}
        {err && <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#9f1239' }}>{err}</div>}

        {state && (
          <>
            <div style={{ background: k.bg, border: `2px solid ${k.bd}`, borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: k.fg, marginBottom: 8 }}>{k.label}</div>
              {state.held ? (
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
                  <div><strong>Held rate:</strong> {state.held.pct}</div>
                  <div><strong>Written:</strong> {new Date(state.held.fetchedAt).toLocaleString()}</div>
                  <div><strong>Source:</strong> {state.held.source || '—'}</div>
                  <div><strong>Entered by:</strong> {state.held.enteredBy || <span style={{ color: '#9f1239' }}>not recorded</span>}</div>
                  <div><strong>Read from:</strong> {state.held.sourceNote || <span style={{ color: '#9f1239' }}>not recorded</span>}</div>
                  {state.staleSince && <div style={{ marginTop: 6 }}><strong>Decision since:</strong> {state.staleSince}</div>}
                  {state.invalidReason && <div style={{ marginTop: 6, color: '#9f1239' }}>{state.invalidReason}</div>}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#374151' }}>No rate on record.</div>
              )}
              {!state.calendar?.ok && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#9f1239', lineHeight: 1.6 }}>{state.calendar.reason}</div>
              )}
            </div>

            <form onSubmit={submit} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Enter the current prime rate</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16, lineHeight: 1.6 }}>
                Both fields are required. The rate must be a multiple of 0.25% — anything else is
                refused and not stored, and is not rounded to the nearest valid figure.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 }}>Rate (%)</label>
                  <input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="7.00" inputMode="decimal" required
                    style={{ width: '100%', padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 }}>Read from</label>
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="WSJ Money Rates, 2026-09-21" required
                    style={{ width: '100%', padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              </div>
              {saveErr && (
                <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, padding: '11px 13px', fontSize: 13, color: '#9f1239', marginBottom: 12, lineHeight: 1.6 }}>{saveErr}</div>
              )}
              <button type="submit" disabled={saving || !rate || !note}
                style={{ padding: '10px 20px', background: (saving || !rate || !note) ? '#e5e7eb' : '#acf44e', color: (saving || !rate || !note) ? '#9ca3af' : '#1a1a1a', border: 'none', borderRadius: 9, fontWeight: 800, fontSize: 13, cursor: (saving || !rate || !note) ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : 'Set prime rate'}
              </button>
            </form>
          </>
        )}

        {toast && (
          <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#111827', color: '#fff', padding: '12px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700 }}>{toast}</div>
        )}
      </div>
    </AdminLayout>
  );
}
